import React, { useCallback, useState } from 'react'
import { Button, Card, Chip, Grid, GridItem, Text, Toggle } from '@/components'
import { AlertMessage, NavigationPage } from '@/app/components'
import { AdaptiveCardRowLayout } from '@/app/layouts'
import { Plan, Thermostat, ThermostatSettings } from '@/types'
import { debounce } from '@/utils/limit'
import { useAppDispatch, useAppSelector } from '@/hooks'
import { thermostatSettingsCollection } from '@/reducers/thermostatSettings'
import { thermostatActiveSettingsCollection } from '@/reducers/thermostatActiveSettings'
import { thermostatsCollection } from '@/reducers/thermostats'
import PageLoader from '@/authenticated/components/layout/PageLoader'
import { useParams } from 'react-router-dom'
import { useBrandedThermostatName } from '@/hooks/useBrandedThermostatName'
import { useNavigation } from '@/app/hooks'

// TOU pre-cool is branded "Save Money". When the plan supports cost category
// control the settings tab routes to the cost-categories Save Money page
// instead of here; this page is the fallback for everyone else.
export type SaveMoneyPageState =
  | 'eligible'
  | 'ineligible'
  | 'planUnknown'
  | 'planPending'
  | 'ecobeeDisabled'

export function getSaveMoneyPageState({
  plan,
  isPlanPending,
  isEcobee,
}: {
  plan: Pick<Plan, 'is_time_of_use'> | null | undefined
  isPlanPending: boolean
  isEcobee: boolean
}): SaveMoneyPageState {
  if (isEcobee) return 'ecobeeDisabled'
  if (plan) return plan.is_time_of_use ? 'eligible' : 'ineligible'
  return isPlanPending ? 'planPending' : 'planUnknown'
}

const BANNER_COPY: Partial<Record<SaveMoneyPageState, string>> = {
  ecobeeDisabled:
    "Save Money's daily pre-cooling is currently unavailable for Ecobees.",
  ineligible:
    "Save Money only works for customers on a time-of-use rate plan. Your current plan doesn't have peak pricing, so there's nothing to shift around. You're still supporting the grid during events.",
  planUnknown:
    'Save Money is only available for customers on a time-of-use rate plan. Add your rate plan to see if Save Money can help.',
  planPending:
    "Save Money is only available for customers on a time-of-use rate plan. We're still reviewing your rate plan — check back soon.",
}

type ViewModelProps = {
  settings: ThermostatSettings | undefined
  thermostatId: Thermostat['id']
  isEcobee: boolean
}

function useViewModel(props: ViewModelProps) {
  const dispatch = useAppDispatch()
  const navigation = useNavigation()

  const user = useAppSelector((state) => state.user.user)
  const plan = user?.profile?.plan ?? null
  const isPlanPending = Boolean(user?.profile?.is_plan_pending)

  const pageState = getSaveMoneyPageState({
    plan,
    isPlanPending,
    isEcobee: props.isEcobee,
  })
  const toggleDisabled = pageState !== 'eligible'

  const [localPrecoolEnabled, setLocalPrecoolEnabled] = useState(
    toggleDisabled ? false : props.settings?.pre_cool_enabled,
  )

  const debouncedUpdateSettings = useCallback(
    debounce((updatedSettings: Partial<ThermostatSettings>) => {
      dispatch(
        thermostatSettingsCollection.actions.update(
          `/thermostats/${props.thermostatId}/settings/`,
          updatedSettings,
          { id: props.settings?.id },
        ),
      )
      dispatch(thermostatActiveSettingsCollection.actions.invalidate())
    }, 500),
    [props.thermostatId, props.settings?.id],
  )

  const brandedThermostatName = useBrandedThermostatName(props.thermostatId)

  const onPrecoolToggle = (checked: boolean) => {
    if (toggleDisabled) return
    setLocalPrecoolEnabled(checked)
    debouncedUpdateSettings({ pre_cool_enabled: checked })
  }

  const navigateToAddRatePlan = () => {
    navigation.push('/connect-utility/select')
  }

  return {
    brandedThermostatName,
    onPrecoolToggle,
    localPrecoolEnabled,
    pageState,
    toggleDisabled,
    navigateToAddRatePlan,
  }
}

function PrecoolPreheatSettingsPageComponent(
  props: ReturnType<typeof useViewModel>,
) {
  const bannerCopy = BANNER_COPY[props.pageState]
  return (
    <NavigationPage
      id="thermostat-precool-preheat-settings-page"
      title="Save Money"
      subtitle={props.brandedThermostatName ?? 'Thermostat'}
      navigationOptions={['back']}
    >
      {/* No top margins on the first elements — NavigationPage's header
          already carries the standard 28px bottom margin. */}
      {bannerCopy && (
        <AlertMessage variant="warning" className="mb-3">
          {bannerCopy}
        </AlertMessage>
      )}
      {/* A real Card (not AdaptiveCardRowLayout.CardRow, which renders flat
          on small screens) — the design shows a white card on mobile too. */}
      <Card className="p-4 mb-2">
        <Grid flow="column">
          <GridItem>
            <Text variant="subheader">Save Money</Text>
            <Text variant="body2">
              Optiwatt pre-cools your home before electricity rates peak each
              day, so you save without thinking about it.
            </Text>
          </GridItem>
          <GridItem className="pt-2 pl-16">
            <Toggle
              id="thermostat-precool-toggle"
              checked={props.toggleDisabled ? false : props.localPrecoolEnabled}
              onChange={props.onPrecoolToggle}
              disabled={props.toggleDisabled}
            />
          </GridItem>
        </Grid>
      </Card>
      {props.pageState === 'planUnknown' && (
        <AdaptiveCardRowLayout.PlainRow>
          <Button
            id="thermostat-save-money-add-rate-plan"
            variant="primary"
            onClick={props.navigateToAddRatePlan}
          >
            Add my rate plan
          </Button>
        </AdaptiveCardRowLayout.PlainRow>
      )}
      {props.pageState === 'eligible' && props.localPrecoolEnabled && (
        <AdaptiveCardRowLayout.PlainRow>
          <div className="flex items-center justify-between w-full">
            <Text variant="body1">Est. annual savings</Text>
            <Chip
              id="thermostat-save-money-savings-pill"
              className="bg-themed-green-100 py-1 px-3 rounded-lg w-fit"
            >
              <Text className="text-themed-green-700">$30-50 / yr</Text>
            </Chip>
          </div>
        </AdaptiveCardRowLayout.PlainRow>
      )}
    </NavigationPage>
  )
}

function PrecoolPreheatSettingsPage(props: ViewModelProps) {
  const vm = useViewModel(props)
  return <PrecoolPreheatSettingsPageComponent {...vm} />
}

function PrecoolPreheatSettingsPageWithFallback(props: {
  thermostatId: Thermostat['id']
}) {
  const { data: settingsArray, status: settingsStatus } =
    thermostatSettingsCollection.useFetch(
      `/thermostats/${props.thermostatId}/settings/`,
    )
  const settings = settingsArray?.[0]
  const settingsLoading = settingsStatus !== 'succeeded' && !settings

  const { data: thermostats } = thermostatsCollection.useFetch()
  const thermostat = thermostats.find((t) => t.id === props.thermostatId)
  const isEcobee = thermostat?.portal === 'Ecobee'

  const user = useAppSelector((state) => state.user.user)

  if (settingsLoading || !user) {
    return <PageLoader />
  }

  return (
    <PrecoolPreheatSettingsPage
      thermostatId={props.thermostatId}
      settings={settings}
      isEcobee={isEcobee}
    />
  )
}

type PathParams = { deviceId: string | undefined }

export default function PrecoolPreheatSettingsPageWithParams() {
  const { deviceId } = useParams<PathParams>()
  if (!deviceId) return <PageLoader />
  const thermostatId = parseInt(deviceId)
  if (isNaN(thermostatId)) return <PageLoader />
  return <PrecoolPreheatSettingsPageWithFallback thermostatId={thermostatId} />
}
